import type { I18N_CONF } from '@funkey/locales/config';
import type { ActivitySearchPage } from '~/features/activities/search/server';
import type { _ACTIVITY_SEARCH_QUERY_OPTIONS } from '~/features/activities/search/shared';
import { canonicalAllowlistIDs } from './rules';

/** Catalog rules → `SearchCustomizationProps`: "the visible catalog, expressed
 * as a search". The single owner of the mode fork (INCLUSIVE ⇒ allowlist in
 * top-level `groupIDs`; EXCLUSIVE ⇒ denylist in `constraints.excludedGroupIDs`),
 * the INCLUSIVE-with-empty-IDs `disabled` invariant, and the bizz-search
 * constants (`includeAds: false`, `weightSet: 'control'`). Consumers feed the
 * output through `_ACTIVITY_SEARCH_QUERY_OPTIONS`, which owns the plumbing of
 * the allowlist into `filters.groupIDs`.
 *
 * The `filters` param is the common subset of `bizz.activityFilters` (DB column)
 * and `Catalog_EditMode_Rules` (client state) — both are structurally
 * assignable, so no adapter is needed. `max` is optional to match the DB
 * column, where the schema leaves it optional. */
export function Catalog_toSearchCustomization(
    filters: {
        groupIDs: { mode: 'INCLUSIVE' | 'EXCLUSIVE'; IDs: number[] | null };
        rootCategoryIDs: number[] | null;
        activityRegionIDs: number[] | null;
        budget: { min: number | null; max?: number | null };
        participants: { min: number | null; max?: number | null };
    },
    tenantId: number | null,
): ActivitySearchPage.SearchCustomizationProps {
    const isInclusive = filters.groupIDs.mode === 'INCLUSIVE';
    // INCLUSIVE allowlist with 0 entries = "no activities visible". Disable the
    // search entirely; otherwise `nullIfEmpty` in `_ACTIVITY_SEARCH_QUERY_OPTIONS`
    // collapses the empty array to `null` (= unconstrained) and the full catalog
    // leaks through.
    const inclusiveEmpty = isInclusive && (filters.groupIDs.IDs?.length ?? 0) === 0;

    return {
        includeAds: false,
        weightSet: 'control',
        disabled: inclusiveEmpty,
        groupIDs: isInclusive ? (filters.groupIDs.IDs ?? undefined) : undefined,
        constraints: {
            budget: filters.budget,
            participants: filters.participants,
            allowedRegions: canonicalAllowlistIDs(filters.activityRegionIDs),
            allowedCategories: canonicalAllowlistIDs(filters.rootCategoryIDs),
            excludedGroupIDs: isInclusive ? null : filters.groupIDs.IDs,
            tenantId,
        },
    };
}

/** First argument to `_ACTIVITY_SEARCH_QUERY_OPTIONS` for a BIZZ catalog search,
 * and the single source of truth for the bizz-derived params — above all the
 * effective `vatRate`. The price/budget filter is vat-sensitive, so the booking
 * grid, the layout exclusion set, the edit-mode tab counts and the CSV export
 * MUST pass the same vatRate or they compute divergent catalogs and bookable
 * activities get flagged "unavailable" (FUN-4725).
 *
 * Each caller supplies its own rules-derived `customization` and its own second
 * argument to `_ACTIVITY_SEARCH_QUERY_OPTIONS` (pagination + user filters). */
export function Catalog_bizzSearchOptions(args: {
    bizz: { id: number; vatInclusive: boolean | null; vatRate: number | null };
    locale: I18N_CONF.Locale;
    customization?: ActivitySearchPage.SearchCustomizationProps;
}): Parameters<typeof _ACTIVITY_SEARCH_QUERY_OPTIONS>[0] {
    return {
        constraints: args.customization?.constraints ?? null,
        groupIDs: args.customization?.groupIDs,
        weightSet: args.customization?.weightSet ?? 'control',
        includeAds: args.customization?.includeAds ?? false,
        locale: args.locale,
        vatRate: args.bizz.vatInclusive ? args.bizz.vatRate : null,
        bizzID: args.bizz.id,
    };
}
